import { type Channel } from '@lmt/signal-viewer-ui';
import { LocalStorageKeys } from '../local-storage';

/**
 * Per-profile display preferences, kept apart from the profile itself so that
 * reordering or hiding a channel never counts as editing the profile's content.
 * Both lists hold channel ids.
 */
export type ChannelView = {
  order?: string[];
  hidden?: string[];
};

export type ChannelViewByProfileId = Record<string, ChannelView>;

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function isChannelView(value: unknown): value is ChannelView {
  if (!value || typeof value !== 'object') return false;

  const v = value as Record<string, unknown>;

  return (
    (v.order === undefined || isStringArray(v.order)) &&
    (v.hidden === undefined || isStringArray(v.hidden))
  );
}

function isChannelViewByProfileId(
  value: unknown,
): value is ChannelViewByProfileId {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.values(value).every(isChannelView)
  );
}

export function readChannelViewFromStorage(): ChannelViewByProfileId {
  const saved = localStorage.getItem(LocalStorageKeys.ChannelView);
  if (!saved) return {};

  try {
    const parsed = JSON.parse(saved);

    if (isChannelViewByProfileId(parsed)) {
      return parsed;
    }

    localStorage.removeItem(LocalStorageKeys.ChannelView);
    return {};
  } catch {
    localStorage.removeItem(LocalStorageKeys.ChannelView);
    return {};
  }
}

export function writeChannelViewToStorage(view: ChannelViewByProfileId) {
  localStorage.setItem(LocalStorageKeys.ChannelView, JSON.stringify(view));
}

/**
 * Stored ids that no longer exist are skipped and channels the templates gained
 * are appended, so template changes surface without resetting the user's order.
 */
export function applyChannelOrder(
  config: Channel[],
  order?: string[],
): Channel[] {
  if (!order?.length) return config;

  const byId = new Map(config.map((channel) => [channel.id, channel]));
  const ordered = order
    .map((id) => byId.get(id))
    .filter((channel) => channel !== undefined);
  const orderedIds = new Set(ordered.map((channel) => channel.id));

  return [
    ...ordered,
    ...config.filter((channel) => !orderedIds.has(channel.id)),
  ];
}
